import { useDialog } from '@dni-hooks/useDialog'
import { useApiCampaignProductSearch } from '@hooks-api/fortune/useApiCampaignProductSearch'
import { type Recommendation } from '@hooks-api/types/dataModel/butler'
import { ErrorFilled } from '@pets-ui/icons'
import { useSnackbar } from '@pets-ui/Snackbar/useSnackbar'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useState } from 'react'
import { useButler } from '../components/ButlerProvider/useButler'
import { DIALOG_CHANGE_BUTLER_ITEM, type DialogChangeButlerItemProps } from '../components/DialogChangeButlerItem/DialogChangeButlerItem'
import { useButlerSearchInput } from './useButlerSearchInput'

interface ExchangeProduct {
  variation_id?: number
  parent_product_id?: number
  sku?: string
  quantity?: number
  stock?: number
}

type OnExchangeProduct = ((exchangeProduct: ExchangeProduct) => void)

export interface UseListItemSearchArgs {
  recommendations?: Recommendation[]
  variationId?: number
  onExchangeProduct?: OnExchangeProduct
}

export const useButlerListItemSearch = (
  {
    recommendations,
    variationId,
    onExchangeProduct,
  }: UseListItemSearchArgs,
) => {
  const { campaignCode } = useButler()
  const { openSnackbar } = useSnackbar()
  const [isExchanging, setIsExchanging] = useState(false)
  const currentProduct = recommendations?.find(recommendation => recommendation.wp_variation_id === variationId)
  const [selectedExchangeProduct, setSelectedExchangeProduct] = useState<ExchangeProduct | undefined>({
    parent_product_id: currentProduct?.wp_product_id,
    variation_id: currentProduct?.wp_variation_id,
  })
  const { closeDialog } = useDialog<DialogChangeButlerItemProps>(DIALOG_CHANGE_BUTLER_ITEM)

  const { query, inputValue, handleClearInputValue: _handleClearInputValue, handleInputChange, handleSearchSubmit: _handleSearchSubmit } = useButlerSearchInput()
  const { destructuredData: searchProducts, isLoading } = useApiCampaignProductSearch({
    code: campaignCode,
    query,
  })

  const clearSelectedProduct = () => setSelectedExchangeProduct(undefined)

  const handleClose = () => {
    closeDialog()
    clearSelectedProduct()
  }

  const handleSelectProduct = (product: ExchangeProduct) => () => {
    setSelectedExchangeProduct(product)
  }

  const handleClearInputValue = () => {
    _handleClearInputValue()
    clearSelectedProduct()
  }

  const handleSearchSubmit = (value: string) => {
    _handleSearchSubmit(value)
    clearSelectedProduct()
  }

  const handleExchangeProduct = async () => {
    if (!onExchangeProduct || !selectedExchangeProduct) return
    try {
      setIsExchanging(true)
      onExchangeProduct(selectedExchangeProduct)
      handleClose()
    }
    catch (error) {
      console.error(error)
      clearSelectedProduct()
      openSnackbar({
        message: '商品替換失敗',
        icon: <ErrorFilled color={colors.red600} />,
      })
    }
    finally {
      setIsExchanging(false)
    }
  }

  return {
    currentProduct,
    selectedExchangeProduct,
    query,
    inputValue,
    searchProducts,
    isLoading,
    isExchanging,
    handleClose,
    handleExchangeProduct,
    handleSelectProduct,
    handleClearInputValue,
    handleInputChange,
    handleSearchSubmit,
  }
}
